import { Check, ChevronDown, Plus } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../../lib/api";
import { createLogger } from "../../lib/logger";
import { useAppStore } from "../../store/useAppStore";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { NewEnvironmentDialog } from "./NewEnvironmentDialog";

const log = createLogger("ProjectEnvironmentSelector");

interface Environment {
  id: string;
  name: string;
  isDefault: boolean;
  description?: string;
}

interface ProjectEnvironmentSelectorProps {
  onEnvironmentChange?: (environment: Environment) => void;
}

export function ProjectEnvironmentSelector({
  onEnvironmentChange,
}: ProjectEnvironmentSelectorProps) {
  const { projectId } = useParams<{ projectId: string }>();
  const { projects } = useAppStore();

  const [environments, setEnvironments] = useState<Environment[]>([]);
  const [selectedProject, setSelectedProject] = useState<any>(null);
  const [selectedEnvironment, setSelectedEnvironment] =
    useState<Environment | null>(null);
  const [loading, setLoading] = useState(true);
  const [showNewEnvironmentDialog, setShowNewEnvironmentDialog] =
    useState(false);

  // Find the current project
  useEffect(() => {
    if (projectId && projects.length > 0) {
      const project = projects.find((p) => p.id === projectId);
      setSelectedProject(project);
    }
  }, [projectId, projects]);

  // Fetch environments for the current project
  const fetchEnvironments = useCallback(async () => {
    if (!projectId) return;

    try {
      setLoading(true);
      const data = await api.environments.listByProject(projectId);
      setEnvironments(data.environments || []);

      if (data.environments && data.environments.length > 0) {
        // Read URL at resolution time to avoid stale closure over searchParams.
        const urlEnvId = new URLSearchParams(window.location.search).get("environmentId");

        if (urlEnvId) {
          // URL already specifies an environment — just reflect it visually.
          // Do NOT call onEnvironmentChange: the parent resolves this independently
          // via its own useEffect and calling it here would overwrite the URL param
          // with the default env, causing a race condition.
          const urlEnv =
            data.environments.find((env: Environment) => env.id === urlEnvId) ||
            data.environments.find((env: Environment) => env.isDefault) ||
            data.environments[0];
          setSelectedEnvironment(urlEnv);
        } else {
          // No env in URL — auto-select the default and notify the parent.
          const defaultEnv =
            data.environments.find((env: Environment) => env.isDefault) ||
            data.environments[0];
          setSelectedEnvironment(defaultEnv);
          onEnvironmentChange?.(defaultEnv);
        }
      }
    } catch (error) {
      log.error("Failed to fetch environments", error);
    } finally {
      setLoading(false);
    }
    // `onEnvironmentChange` is intentionally excluded: it is a parent-supplied
    // callback that is often re-created each render, and including it would
    // re-create fetchEnvironments on every parent render and re-trigger the
    // fetch effect below in a loop. We only want to refetch when projectId changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    fetchEnvironments();
  }, [fetchEnvironments]);

  const handleEnvironmentChange = useCallback(
    (environment: Environment) => {
      setSelectedEnvironment(environment);
      onEnvironmentChange?.(environment);
    },
    [onEnvironmentChange]
  );

  const handleNewEnvironmentCreated = () => {
    fetchEnvironments();
    setShowNewEnvironmentDialog(false);
  };

  if (loading) {
    return (
      <div className="flex items-center space-x-2">
        <div className="h-8 w-32 bg-muted animate-pulse rounded"></div>
        <div className="h-8 w-24 bg-muted animate-pulse rounded"></div>
      </div>
    );
  }

  return (
    <div className="flex items-center space-x-2">
      {/* Environment Selector */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" className="flex items-center space-x-2">
            <span className="font-medium">
              {selectedEnvironment?.name || "Select Environment"}
            </span>
            <ChevronDown className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-56">
          <DropdownMenuLabel>Environments</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {environments.map((environment) => (
            <DropdownMenuItem
              key={environment.id}
              onClick={() => handleEnvironmentChange(environment)}
              className="flex items-center justify-between"
            >
              <div className="flex flex-col">
                <span>{environment.name}</span>
                {environment.description && (
                  <span className="text-xs text-muted-foreground">
                    {environment.description}
                  </span>
                )}
              </div>
              {environment.id === selectedEnvironment?.id && (
                <Check className="h-4 w-4 text-primary" />
              )}
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => setShowNewEnvironmentDialog(true)}>
            <Plus className="h-4 w-4 mr-2" />
            New Environment
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* New Environment Dialog */}
      {showNewEnvironmentDialog && selectedProject && (
        <NewEnvironmentDialog
          projectId={selectedProject.id}
          onClose={() => setShowNewEnvironmentDialog(false)}
          onSuccess={handleNewEnvironmentCreated}
        />
      )}
    </div>
  );
}
